import React, { useState, useEffect } from 'react';
import { trainingApi } from '../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchDashboard();
    // Refresh every 60 seconds
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await trainingApi.getAdminDashboard();
      setDashboardData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching admin dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortPMs = (pms) => {
    if (!pms) return [];
    
    return [...pms].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = a.pm.name.toLowerCase();
          bVal = b.pm.name.toLowerCase();
          break;
        case 'completion':
          aVal = a.training?.completionPercentage || 0;
          bVal = b.training?.completionPercentage || 0;
          break;
        case 'orders':
          aVal = a.workload?.totalOrders || 0;
          bVal = b.workload?.totalOrders || 0;
          break;
        case 'value':
          aVal = a.workload?.totalValue || 0;
          bVal = b.workload?.totalValue || 0;
          break;
        case 'certification':
          aVal = a.pm.certification_level || 'None';
          bVal = b.pm.certification_level || 'None';
          break;
        default:
          aVal = a.pm.name.toLowerCase();
          bVal = b.pm.name.toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  if (loading && !dashboardData) {
    return (
      <div className="admin-dashboard">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button onClick={fetchDashboard} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const pms = sortPMs(dashboardData?.pms || []);
  const quickbaseStatus = dashboardData?.quickbaseStatus;

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>👑 Manager Dashboard</h1>
        <div className="refresh-info">
          <span className="status-indicator">
            {quickbaseStatus === 'connected' ? '🟢 Quickbase Connected' : '🟡 Quickbase Unavailable'}
          </span>
          <button onClick={fetchDashboard} className="refresh-btn" disabled={loading}>
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total PMs</h3>
            <p className="stat-value">{stats.totalPMs}</p>
            <span className="stat-detail">{stats.activePMs} active</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>Total Orders</h3>
            <p className="stat-value">{stats.totalOrders}</p>
            <span className="stat-detail">Next 12 weeks</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Total Value</h3>
            <p className="stat-value">${(stats.totalOrderValue || 0).toLocaleString()}</p>
            <span className="stat-detail">Order value</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-content">
            <h3>Avg Completion</h3>
            <p className="stat-value">{stats.averageCompletionRate}%</p>
            <span className="stat-detail">Training progress</span>
          </div>
        </div>
      </div>

      {/* Certification Distribution */}
      <div className="cert-distribution">
        <h2>Certification Levels</h2>
        <div className="cert-badges">
          {Object.entries(stats.certificationDistribution || {}).map(([level, count]) => (
            <div key={level} className={`cert-badge cert-${level.toLowerCase()}`}>
              <span className="cert-label">{level}</span>
              <span className="cert-count">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* PM Table */}
      <div className="pm-table-container">
        <h2>Project Manager Overview</h2>
        <table className="pm-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable">
                Name {sortBy === 'name' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th>Email</th>
              <th onClick={() => handleSort('certification')} className="sortable">
                Certification {sortBy === 'certification' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('completion')} className="sortable">
                Training Progress {sortBy === 'completion' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th>Modules</th>
              <th>Avg Score</th>
              <th onClick={() => handleSort('orders')} className="sortable">
                Orders {sortBy === 'orders' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('value')} className="sortable">
                Order Value {sortBy === 'value' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
            </tr>
          </thead>
          <tbody>
            {pms.map((pm) => (
              <tr key={pm.pm.id} className={pm.pm.role === 'Manager' ? 'manager-row' : ''}>
                <td>
                  <div className="pm-name">
                    {pm.pm.role === 'Manager' && '👑 '}
                    <strong>{pm.pm.name}</strong>
                  </div>
                </td>
                <td>{pm.pm.email}</td>
                <td>
                  <span className={`cert-pill cert-${(pm.pm.certification_level || 'none').toLowerCase()}`}>
                    {pm.pm.certification_level || 'None'}
                  </span>
                  {pm.pm.certification_target && pm.pm.certification_target !== pm.pm.certification_level && (
                    <span className="cert-target"> → {pm.pm.certification_target}</span>
                  )}
                </td>
                <td>
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${pm.training?.completionPercentage || 0}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">{pm.training?.completionPercentage || 0}%</span>
                  </div>
                </td>
                <td>
                  {pm.training ? (
                    <span>
                      <span className="module-complete">{pm.training.completedModules}</span>
                      /{pm.training.totalModules}
                      {pm.training.inProgressModules > 0 && (
                        <span className="module-progress"> (+{pm.training.inProgressModules})</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  {pm.training?.averageScore > 0 ? (
                    <span className={`score ${pm.training.averageScore >= 70 ? 'pass' : 'fail'}`}>
                      {pm.training.averageScore}%
                    </span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  {pm.workload ? (
                    <div className="workload-info">
                      <strong>{pm.workload.totalOrders}</strong>
                      {pm.workload.trainingProjects > 0 && (
                        <span className="training-projects"> (+{pm.workload.trainingProjects} training)</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  {pm.workload ? (
                    <strong>${pm.workload.totalValue.toLocaleString()}</strong>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
